export class CookieConsentUtils {
  /**
   * @description
   * Get the consent properties for accepting all.
   *
   * @returns {ConsentProperties}
   */
  static getAcceptAll() {
    return this.getConsentProperties(true);
  }

  /**
   * @description
   * Get the consent properties for declining all.
   *
   * @returns {ConsentProperties}
   */
  static getDeclineAll() {
    return this.getConsentProperties(false);
  }

  /**
   * @description
   * Functional equivalent to {@link getDeclineAll}, but named to describe usage.
   * @see {@link getDeclineAll}
   * @function
   * @name getDefaultConsent
   */
  static getDefaultConsent() {
    return this.getConsentProperties(false);
  }

  /**
   * @description
   * Get consent properties with a given initial value.
   *
   * @param {boolean} initialValue - initial value for all editable consent types
   * @returns {ConsentProperties}
   */
  static getConsentProperties(initialValue = false) {
    return {
      ...this.getEditableConsentProperties(initialValue),
      ...this.getNecessaryConsentProperties(),
    };
  }

  /**
   * @description
   * Get the editable consent properties.
   *
   * @returns {Partial<ConsentProperties>}
   */
  static getEditableConsentProperties(initialValue = false) {
    return {
      analytics: initialValue,
      preferences: initialValue,
      marketing: initialValue,
    };
  }

  /**
   * @description
   * Get the necessary consent properties.
   *
   * @returns {Partial<ConsentProperties>}
   */
  static getNecessaryConsentProperties() {
    return {
      necessary: true,
    };
  }

  /**
   * @description
   * Map the consent properties to Google Tag Manager preferences.
   *
   * NOTE: Properties are used to bind to the form fields, and
   * preferences are used to send to Google Tag Manager.
   *
   * @typedef {Object} ConsentPreferences
   * @property {'granted' | 'denied'} ConsentPreferences.security_storage
   * @property {'granted' | 'denied'} ConsentPreferences.analytics_storage
   * @property {'granted' | 'denied'} ConsentPreferences.functionality_storage
   * @property {'granted' | 'denied'} ConsentPreferences.personalization_storage
   * @property {'granted' | 'denied'} ConsentPreferences.ad_storage
   *
   * @param {ConsentProperties} consentProperties - the consent properties to map
   *
   * @returns {ConsentPreferences} - the mapped consent preferences
   */
  static mapConsentPropertiesToPreferences(consentProperties) {
    const getPreference = (condition) => (condition ? 'granted' : 'denied');

    const consentPreferences = {
      security_storage: getPreference(consentProperties.necessary),
      analytics_storage: getPreference(consentProperties.analytics),
      functionality_storage: getPreference(consentProperties.preferences),
      personalization_storage: getPreference(consentProperties.preferences),
      ad_storage: getPreference(consentProperties.marketing),
    };

    return consentPreferences;
  }

  /**
   * @description
   * Map the Google Tag Manager preferences to consent properties.
   *
   * NOTE: Properties are used to bind to the form fields, and
   * preferences are used to send to Google Tag Manager.
   *
   * @typedef {Object} ConsentProperties
   * @property {boolean} ConsentProperties.necessary - necessary consent (always true)
   * @property {boolean} ConsentProperties.analytics - analytics consent
   * @property {boolean} ConsentProperties.preferences - preferences consent
   * @property {boolean} ConsentProperties.marketing - marketing consent
   *
   * @param {ConsentPreferences} consentPreferences - the consent preferences to map
   *
   * @returns {ConsentProperties} - the mapped consent properties
   */
  static mapConsentPreferencesToProperties(consentPreferences) {
    const getProperties = (condition) => condition === 'granted';

    const consentProperties = {
      ...CookieConsentUtils.getNecessaryConsentProperties(),
      analytics: getProperties(consentPreferences.analytics_storage),
      preferences: getProperties(consentPreferences.personalization_storage),
      marketing: getProperties(consentPreferences.ad_storage),
    };

    return consentProperties;
  }
}
